import React, {useState} from 'react';

import {
    EventDescriptionGenerationView,
} from 'src/modules/ai/event-description-generation/event-description-generation.view';
import {
    changeRequestValidation,
} from 'src/modules/ai/event-description-generation/validations/change-request-validation';
import {createTypingEffect} from 'src/modules/ai/utils/createTypingEffect';
import {serializeAttributeTypeFields} from 'src/modules/ai/utils/serializeAttributeTypeFields';
import {generateEventDescription} from 'src/openapi/apis/EventDataGeneratorControllerApi';
import {EventChangeRequest} from 'src/openapi/models/EventChangeRequest';
import {useAppDispatch} from 'src/store';

import {Icons} from '@pb/kit-component-icon';
import {Status, usePushNotification} from '@pb/kit-component-push-notification';

type Props = {
    eventChangeRequest: EventChangeRequest | undefined,
    onEventDescriptionGenerate?: (description: string) => void,
};

const GENERATE_EVENT_DESCRIPTION_ERROR_MESSAGE = 'Failed to generate event description. Please try again.';
const ERROR_AUTO_HIDE_TIMEOUT = 10000;

export const EventDescriptionGenerationController = (props: Props) => {
    const {
        eventChangeRequest,
        onEventDescriptionGenerate,
    } = props;

    const dispatch = useAppDispatch();
    const {onCreateNote} = usePushNotification();
    const [onGenerating, setOnGenerating] = useState(false);
    const [eventDescription, setEventDescription] = useState('');

    const onGenerateClick = async () => {
        if (eventChangeRequest) {
            if (changeRequestValidation(eventChangeRequest).length) {
                changeRequestValidation(eventChangeRequest).forEach(error => {
                    onCreateNote({
                        id: error,
                        message: error,
                        status: Status.ERROR,
                        buttonProps: {iconProps: {type: Icons.CLOSE}},
                        autoHideTimeout: ERROR_AUTO_HIDE_TIMEOUT,
                    });
                });
                return;
            }

            setOnGenerating(true);

            const request = {
                generateEventDescriptionRequest: {
                    ...serializeAttributeTypeFields(eventChangeRequest),
                    description: eventChangeRequest.description,
                    trigger: eventChangeRequest.trigger,
                    openApiDiscriminator: 'GenerateEventDescriptionRequest',
                },
            };

            const response = await dispatch(generateEventDescription(request));

            if (response.payload.success) {
                createTypingEffect(response.payload.data, setEventDescription);
            }

            if (response.payload.error) {
                onCreateNote({
                    id: 'generateEventDescription',
                    status: Status.ERROR,
                    message: GENERATE_EVENT_DESCRIPTION_ERROR_MESSAGE,
                    buttonProps: {iconProps: {type: Icons.CLOSE}},
                    autoHideTimeout: 10000,
                });
            }

            setOnGenerating(false);
        }
    };

    return (
        <EventDescriptionGenerationView
            eventDescription={eventDescription}
            setEventDescription={setEventDescription}
            onGenerateClick={onGenerateClick}
            onGenerating={onGenerating}
            onEventDescriptionGenerate={onEventDescriptionGenerate}
        />
    );
};
